// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

/* This file provides MSAL auth configuration to get access token through nested app authentication. */

/* global console */

import { createNestablePublicClientApplication, type IPublicClientApplication } from "@azure/msal-browser";

export { AccountManager };

const applicationId = "eca856fc-c5e0-4fe4-af89-ec28e348da85";

const msalConfig = {
  auth: {
    clientId: applicationId,
    authority: "https://login.microsoftonline.com/common",
    supportsNestedAppAuth: true,
  },
};

// Encapsulate functions for getting user account and token information.
class AccountManager {
  pca: IPublicClientApplication | undefined = undefined;

  // Initialize MSAL public client application.
  async initialize() {
    this.pca = await createNestablePublicClientApplication(msalConfig);
  }

  async ssoGetToken(scopes: string[]) {
    const userAccount = await this.ssoGetUserIdentity(scopes);
    return userAccount.accessToken;
  }

  /**
   * Uses MSAL and nested app authentication to get the user account from Office SSO.
   * This demonstrates how to work with user identity from the token.
   *
   * @returns The user account data (identity).
   */
  async ssoGetUserIdentity(scopes: string[]) {
    if (this.pca === undefined) {
      throw new Error("AccountManager is not initialized!");
    }

    // Specify minimum scopes needed for the access token.
    const tokenRequest = {
      scopes: scopes
    };

    try {
      const userAccount = await this.pca.acquireTokenSilent(tokenRequest);
      return userAccount;
    } catch (error) {
    }

    // Acquire token silent failure. Send an interactive request via popup.
    try {
      const userAccount = await this.pca.acquireTokenPopup(tokenRequest);
      return userAccount;
    } catch (popupError) {
      // Acquire token interactive failure.
      throw new Error(`Unable to acquire access token: ${popupError}`);
    }
  }
}
